/**
 * Load bundled login hints (shared-storage compatible shape).
 * Tries /login-hints.local.json first (gitignored), then /login-hints.json.
 *
 * For ad-hoc testing, hints may also be passed on the URL (see getStarfleetLoginHintsFromQueryString).
 */

/**
 * Decode a raw query-string value without form-urlencoded '+' → space.
 * decodeURIComponent expands every valid %XX (so encoded handoffs work for any
 * symbol) and leaves bare '+', '@', etc. alone (so unencoded Jarvis handoffs work).
 * Soft-decodes malformed % sequences instead of throwing, expanding valid
 * percent-encoded UTF-8 runs as a whole (not byte-at-a-time).
 * @param {string} raw
 * @returns {string}
 */
function decodeQueryValue(raw) {
  if (!raw) return '';
  try {
    return decodeURIComponent(raw);
  } catch {
    // Soft-decode: expand runs of valid %XX as UTF-8 (not byte-at-a-time),
    // leave malformed % sequences (e.g. %ZZ) untouched in the same string.
    return raw.replace(/(?:%[0-9A-Fa-f]{2})+/g, (run) => {
      try {
        return decodeURIComponent(run);
      } catch {
        const bytes = new Uint8Array(run.length / 3);
        for (let i = 0; i < run.length; i += 3) {
          bytes[i / 3] = parseInt(run.slice(i + 1, i + 3), 16);
        }
        return new TextDecoder('utf-8', { fatal: false }).decode(bytes);
      }
    });
  }
}

/**
 * Read the first matching query param as a raw (still-encoded) substring.
 * Avoids URLSearchParams, which would turn bare '+' into a space.
 * @param {string} search window.location.search
 * @param {string[]} names
 * @returns {string}
 */
function getRawQueryParam(search, names) {
  for (const name of names) {
    const match = search.match(new RegExp(`[?&]${name}=([^&]*)`));
    if (match) return match[1];
  }
  return '';
}

/**
 * Read Starfleet IdP selection hints from the current page URL (dev / launcher handoff).
 * Supports Starfleet-style names and camelCase: login_hint | loginHint, idp_id | idpId.
 *
 * Handles both percent-encoded handoffs (UAS: ruj%2B01%40nvidia.com) and
 * unencoded ones (Jarvis: ruj+02@nvidia.com). Any %XX in the value is decoded;
 * characters that do not need encoding in a query value (+, @, !, *, etc.) pass through.
 *
 * Limit: if a launcher leaves '&' or '#' unencoded inside the email, the URL is
 * already structurally broken before JS runs — those must be percent-encoded by
 * the caller.
 *
 * @returns {{ idpId: string, loginHint: string } | null}
 */
export function getStarfleetLoginHintsFromQueryString() {
  if (typeof window === 'undefined') return null;
  const search = window.location.search || '';
  const loginHint = decodeQueryValue(
    getRawQueryParam(search, ['login_hint', 'loginHint'])
  ).trim();
  const idpId = decodeQueryValue(
    getRawQueryParam(search, ['idp_id', 'idpId'])
  ).trim();
  if (!loginHint || !idpId) return null;
  return { idpId, loginHint };
}

export const STARFLEET_LOGIN_HINTS_STORAGE_KEY = 'prhs.starfleet.login_hints';

/**
 * Persist the hints that drove a silent login so they survive the OAuth
 * roundtrip. The callback navigates to '/' with replace, which strips
 * ?login_hint&idp_id from the URL, and login-hints.json is empty outside local
 * dev — so without this the hints are gone for good after the first sign-in and
 * a later re-auth (id_token expiry mid-session) has nothing to redirect with.
 *
 * Holds the user's email, same as the id_token already stored beside it. Both
 * are sessionStorage, so both die with the tab.
 *
 * @param {{ idpId: string, loginHint: string }} hints
 */
export function persistStarfleetLoginHints(hints) {
  if (typeof window === 'undefined') return;
  if (!hints?.loginHint || !hints?.idpId) return;
  try {
    window.sessionStorage.setItem(
      STARFLEET_LOGIN_HINTS_STORAGE_KEY,
      JSON.stringify({ idpId: hints.idpId, loginHint: hints.loginHint })
    );
  } catch {
    // sessionStorage may be blocked (private mode, ITP); silent login still
    // works this page load, it just won't survive the roundtrip.
  }
}

/**
 * @returns {{ idpId: string, loginHint: string } | null}
 */
export function restoreStarfleetLoginHints() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(STARFLEET_LOGIN_HINTS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const idpId = typeof parsed?.idpId === 'string' ? parsed.idpId.trim() : '';
    const loginHint =
      typeof parsed?.loginHint === 'string' ? parsed.loginHint.trim() : '';
    if (!idpId || !loginHint) return null;
    return { idpId, loginHint };
  } catch {
    return null;
  }
}

export function clearStarfleetLoginHints() {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(STARFLEET_LOGIN_HINTS_STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * @param {string} applicationName e.g. PRHS — must match config.starfleetApplicationName
 * @returns {Promise<{ idpId: string, loginHint: string } | null>}
 */
export async function fetchLoginHintsForApplication(applicationName) {
  if (!applicationName || typeof window === 'undefined') return null;

  const paths = ['/login-hints.local.json', '/login-hints.json'];
  for (const path of paths) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000);
      let res;
      try {
        res = await fetch(path, { cache: 'no-store', signal: controller.signal });
      } finally {
        clearTimeout(timeoutId);
      }
      if (!res.ok) continue;
      const data = await res.json();
      if (!Array.isArray(data)) continue;
      const row = data.find(
        item =>
          item &&
          typeof item.applicationName === 'string' &&
          item.applicationName === applicationName &&
          typeof item.idpId === 'string' &&
          item.idpId.trim() &&
          typeof item.loginHint === 'string' &&
          item.loginHint.trim()
      );
      if (row) {
        return { idpId: row.idpId.trim(), loginHint: row.loginHint.trim() };
      }
    } catch {
      continue;
    }
  }
  return null;
}
